import {
  Banner,
  Button,
  ButtonGroup,
  Card,
  color,
  FormGroup,
  Icon,
  Input,
  Modal,
  spacing,
  textStyleBody,
  ToggleButton,
  Tooltip,
  typography,
  Typography
} from '@uniquegood/realworld-studio-design';
import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Controller, useForm } from 'react-hook-form';
import { Redirect, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { AxiosError } from 'axios';
import useModalState from '@/hooks/useModalState';
import {
  MarginTop24,
  MarginLeft16,
  RowFlex,
  MarginBottom10,
  OnMobileFlexColumn,
  TextAlignCenter,
  ATagStyle
} from '@/styles';
import BottomFloatingBar, { SpacingForBottomFloatingBar } from '@/components/BottomFloatingBar';
import { emitServerErrorToast, helpLink, inputNumberWithCommaFormatter, toast } from '@/utils';
import { coreApi } from '@/api';

import { container960style } from '@/styles/containerStyles';
import LoadingSpinner from '@/components/LoadingSpinner';
import { onMobile } from '@/styles/responsive';

import useRequest from '@/hooks/useRequest';
import {
  useRestrictProjectRoute,
  useWrapFormToConsiderWhitespacesAsEmpty,
  useDynamicMutation,
  usePlanLimits,
  usePromotionModal
} from '@/hooks';
import useIsReadyToRelease from './hooks/useIsReadyToRelease';

import { EmojiRightSpace } from './styles';
import {
  ClickEventName,
  InputEventName,
  PageViewEventName,
  track,
  PromotionTriggerLocation
} from '@/track';
import { ModalChildrenStyles } from '../detail/styles';
import DeleteProjectModal from '@/pages/home/sections/components/DeleteProjectModal';

const payButtonUrl = 'https://rwd.to/studio_paybutton_2208';

const STATUS_DESCRIPTION = {
  Unreleased: {
    title: '콘텐츠를 제작중일 때 사용해요',
    contents: [
      { id: 1, content: '검색 시 리스트에 미노출' },
      { id: 2, content: '콘텐츠 상세정보 확인 불가능' },
      { id: 3, content: '플레이 불가능' }
    ]
  },
  Preparing: {
    title: '출시 전 콘텐츠를 미리 보여주거나, 운영을 잠시 중단할 때 사용해요.',
    contents: [
      { id: 1, content: '검색 시 리스트에 노출' },
      { id: 2, content: '콘텐츠 상세정보 확인 가능' },
      { id: 3, content: '플레이 불가능' }
    ]
  },
  Released: {
    title: '개봉박두! 리얼월드 앱에 콘텐츠을 공개해요.',
    contents: [
      { id: 1, content: '검색 시 리스트에 노출' },
      { id: 2, content: '콘텐츠 상세정보 확인 가능' },
      { id: 3, content: '플레이 가능' }
    ]
  }
};

const PLAN_NAME_MAP: Record<Extract<PlanType, 'Enterprise' | 'Class'>, string> = {
  Enterprise: '엔터프라이즈',
  Class: '클래스'
};

interface FormData extends Pick<Project, 'status' | 'preparationMessage'> {
  price?: string;
  originalPrice?: string;
  pointPrice?: string;
  originalPointPrice?: string;
  isSupporting?: boolean;
  isPaid?: boolean;
}

export default function Release() {
  useEffect(() => {
    track.onPageView({ pageViewEventName: PageViewEventName.view_releasemanage });
  }, []);

  const { appId, projectId } = useParams<AppParam>();

  const readyToRelease = useIsReadyToRelease();
  const isFetchingReadyToRelease = Object.values(readyToRelease).some(
    (value) => value === undefined
  );
  const isReadyToRelease =
    readyToRelease.isRequiredInfosFilled &&
    readyToRelease.hasFullScenario &&
    readyToRelease.hasVisibleMission &&
    readyToRelease.isPlayed;

  const { data: app, mutate: mutateApp } = useRequest<RealWorldApp>(`/apps/${appId}`);

  const { modal, openModal, closeModal } = useModalState();
  const { openPromotionModal } = usePromotionModal();

  const { data: releaseStatus, mutate: mutateReleaseStatus } = useRequest<
    CommonResponseModel<ReleaseStatus>
  >(
    `/apps/${appId}/projects/${projectId}/releaseStatus`,
    {},
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );

  // 프로젝트 목록 조회 (개수 체크용)
  const { data: projects } = useRequest<CommonResponseModel<Project[]>>(
    `/apps/${appId}/projects`,
    {},
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );

  const { hasReached } = usePlanLimits();
  const projectCount = projects?.data?.length ?? 0;

  const { mutateByPath, isLoading: isDeletingProject } = useDynamicMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState<string | null>(null);

  const methods = useForm<FormData>();
  const { control, handleSubmit, formState, watch } =
    useWrapFormToConsiderWhitespacesAsEmpty(methods);

  const redirectTo = useRestrictProjectRoute();

  const isKitRequired = releaseStatus?.data.projectType.includes('Kit');
  const priceFieldRules = isKitRequired ? undefined : { min: 20 };
  const hasPaidPrice = isKitRequired
    ? (releaseStatus?.data.price ?? 0) > 0
    : (releaseStatus?.data.pointPrice ?? 0) > 0;
  const watchedIsPaid = watch('isPaid');
  const isPaid = watchedIsPaid ?? hasPaidPrice;

  // (무료) 유료 기능 포함 콘텐츠 공개 시도시
  const displayContentNotAllowedModal = () => {
    openModal({
      title: '콘텐츠를 공개할 수 없어요',
      children: (
        <>
          <Typography
            type="body"
            cssStyle={[
              MarginBottom10,
              css`
                white-space: pre-line;
              `
            ]}
          >
            {`무료 요금제에서 제한된 기능이 포함되어 있어 콘텐츠 공개가 불가해요. \n아래 중 하나를 선택하여 진행해 주세요.`}
          </Typography>
          <Typography type="body">1. 요금제 업그레이드</Typography>
          <Typography type="body">2. [미션 &gt; 퀘스트]에서 사용 불가능한 기능 제거</Typography>
          <Typography
            type="body"
            textColor="critical"
            cssStyle={css`
              margin-top: ${spacing.margin.large};
              margin-bottom: ${spacing.margin.small};
              font-weight: ${typography.weight.medium};
            `}
          >
            무료 요금제 제한 기능
          </Typography>
          <ul
            css={css`
              list-style: disc;
              margin-left: 20px;
            `}
          >
            <li>
              <Typography as="span" type="body">
                디바이스 연동 도구 (NFC 태그)
              </Typography>
            </li>
            <li>
              <Typography as="span" type="body">
                카메라 기반 실감 기술 도구 (프레임 촬영, QR, AR, 컬러 렌즈, 오버랩)
              </Typography>
            </li>
            <li>
              <Typography as="span" type="body">
                위치 기반 상호 작용 도구를 사용한 경우 (GPS 특정 장소 이동)
              </Typography>
            </li>
            <li>
              <Typography as="span" type="body">
                아이템 - 30개 초과
              </Typography>
            </li>
          </ul>
        </>
      ),
      primaryAction: {
        content: '확인',
        onAction: closeModal
      },
      secondaryAction: {
        content: '요금제 업그레이드',
        onAction: () => {
          closeModal();
          openPromotionModal({
            triggerLocation: PromotionTriggerLocation.content_publish_advanced_action
          });
        }
      }
    });
  };

  const displayLimitExceededModal = (plan: PlanType) => {
    let content;

    if (plan === 'Free') {
      content =
        '요금제가 무료로 전환되어 콘텐츠 수정사항을 저장할 수 없습니다. 콘텐츠를 3개 이하로 줄이거나 요금제를 업그레이드해 주세요.';
    } else if (plan === 'Pro') {
      content =
        '요금제가 프로로 전환되어 콘텐츠 수정사항을 저장할 수 없습니다. 콘텐츠를 10개 이하로 줄이거나 요금제를 업그레이드해 주세요.';
    }

    // content가 없으면 모달을 표시하지 않음
    if (!content) {
      return;
    }

    openModal({
      title: '수정사항을 저장할 수 없어요',
      children: <Typography type="body">{content}</Typography>,
      primaryAction: {
        content: '확인',
        onAction: closeModal
      },
      secondaryAction: {
        content: '요금제 업그레이드',
        onAction: () => {
          closeModal();
          openPromotionModal({
            triggerLocation: PromotionTriggerLocation.content_save_blocked_content_limit
          });
        }
      }
    });
  };

  const onSubmit = handleSubmit(async (data) => {
    if (data.status === 'Released') {
      const rawApp = await mutateApp();
      const plan = rawApp?.planType;

      if (!plan) {
        return;
      }

      // (무료, 프로) 콘텐츠 제한 개수 초과 - 저장 불가
      const isProjectLimitReached = hasReached('projects', projectCount);
      if (isProjectLimitReached && (plan === 'Free' || plan === 'Pro')) {
        displayLimitExceededModal(plan);
        return;
      }

      openModal({
        title: '게임을 공개하시겠어요?',
        children: (
          <>
            <p css={MarginBottom1em}>
              공개 즉시 <strong>리얼월드 앱</strong>에 노출되어요.
            </p>
            <p>
              공개 전 게임이 <strong>완성 상태</strong>인지 또{' '}
              <Button url={helpLink.notion.guideline} external>
                커뮤니티 가이드라인
              </Button>
              을 위반하지 않았는지 마지막으로 확인해주세요!
            </p>
          </>
        ),
        primaryAction: {
          content: '게임 출시!',
          onAction: async () => {
            try {
              await submit();
              publishedModal();
            } catch (error) {
              // NotAllowedPlan 에러는 submit() 내부에서 모달로 처리되므로 토스트 표시하지 않음
              const errorTitle = (error as AxiosError<{ errorTitle: string }>)?.response?.data
                .errorTitle;
              if (errorTitle !== 'NotAllowedPlan') {
                emitServerErrorToast(error);
              }
            }
          }
        },
        secondaryAction: { content: '취소', onAction: closeModal }
      });
      return;
    }

    await submit();

    openModal({
      title: '출시 정보를 수정했어요',
      cssStyle: TextAlignCenter,
      children: (
        <>
          <Icon
            cssStyle={MarginBottom10}
            icon="check_circle_regular"
            size="100px"
            color={color.interactive_primary_subdued}
          />
          수정된 정보가 잘 반영되었는지 리얼월드 앱에서 확인해주세요
        </>
      ),
      primaryAction: { content: '확인', onAction: closeModal }
    });

    async function submit() {
      setIsSubmitting(true);
      const isPaid = data.isPaid === true;

      const body = (() => {
        if (isKitRequired) {
          if (isPaid)
            return {
              price: removeCommaAndParseInt(data.price),
              originalPrice: removeCommaAndParseInt(data.originalPrice)
            };

          return { price: 0, originalPrice: 0 };
        }
        if (isPaid)
          return {
            price: removeCommaAndParseInt(data.price),
            originalPrice: removeCommaAndParseInt(data.originalPrice),
            pointPrice: removeCommaAndParseInt(data.pointPrice),
            originalPointPrice: removeCommaAndParseInt(data.originalPointPrice)
          };
        return { price: 0, originalPrice: 0, pointPrice: 0, originalPointPrice: 0 };  
      })();

      try {
        await coreApi.put<FormData>(
          `/apps/${appId}/projects/${projectId}/releaseStatus`,
          {
            ...data,
            ...body
          },
          {
            headers: {
              'x-rwd-api-version': '1.1'
            },
            skipGlobalErrorToast: true // NotAllowedPlan 에러는 모달로 처리하므로 전역 토스트 비활성화
          }
        );

        await mutateReleaseStatus();

        setIsSubmitting(false);
      } catch (error) {
        setIsSubmitting(false);
        const errorTitle = (error as AxiosError<{ errorTitle: string }>)?.response?.data.errorTitle;
        if (errorTitle === 'NotAllowedPlan') {
          displayContentNotAllowedModal();
          throw error; // 모달을 표시했으므로 에러를 throw하지 않음
        } else {
          emitServerErrorToast(error);
          throw error; // onSubmit에서 에러를 인지하도록 throw
        }
      }

      function removeCommaAndParseInt(value: string | undefined) {
        if (value) {
          const parsedValue = parseInt(value.replaceAll(',', ''), 10);
          return Number.isNaN(parsedValue) ? 0 : parsedValue;
        }

        return 0;
      }
    }

    function publishedModal() {
      openModal({
        title: '게임을 공개했어요 🎉',
        cssStyle: TextAlignCenter,
        children: (
          <>
            <Icon
              cssStyle={MarginBottom10}
              icon="check_circle_regular"
              size="100px"
              color={color.interactive_primary_subdued}
            />
            리얼월드 앱에서 정상적으로 출시되었는지 확인해주세요. 게임을 검색하면 쉽게 확인할 수
            있어요.
          </>
        ),
        primaryAction: { content: '확인', onAction: closeModal }
      });
    }
  });

  const isBeforeUseReleasePermission =
    !!app?.releasePermission && dayjs().isBefore(app.releasePermission.startsAt);

  useEffect(() => {
    if (app?.channelType === 'Business') {
      if (!app.releasePermission) {
        openModal({
          title: '비즈니스 요금제에 가입해주세요!',
          cssStyle: textStyleBody,
          children: (
            <>
              <p>비즈니스 채널에서 게임을 출시하려면 요금제 가입이 필요해요.</p>
              <p>
                자세한 내용은{' '}
                <Button url={payButtonUrl} external>
                  공지
                </Button>
                를 확인해주세요!
              </p>
            </>
          ),
          primaryAction: {
            content: '공지 확인하기',
            type: 'basic',
            onAction: () => window.open(payButtonUrl, '_blank')
          }
        });
        return;
      }

      if (isBeforeUseReleasePermission) {
        const formatTemplate = 'YYYY-MM-DD';
        const formattedTime = dayjs(app.releasePermission.startsAt).format(formatTemplate);
        openModal({
          title: '출시 가능한 기간이 아니에요!',
          cssStyle: textStyleBody,
          children: (
            <>
              아직 게임을 출시할 수 있는 기간이 아닙니다!
              <br />
              <Typography as="span" type="body" textColor="subdued">
                (이용 시작 예정일: <time dateTime={formattedTime}>{formattedTime}</time>)
              </Typography>
            </>
          ),
          primaryAction: {
            content: '닫기',
            onAction: closeModal
          }
        });
        return;
      }
    }

    if (isReadyToRelease === false) {
      const releaseConditionChecks = {
        isRequiredInfosFilled: readyToRelease.isRequiredInfosFilled,
        hasFullScenario: readyToRelease.hasFullScenario,
        hasVisibleMission: readyToRelease.hasVisibleMission,
        isPlayed: readyToRelease.isPlayed
      };
      const failedReleaseConditions = Object.entries(releaseConditionChecks)
        .filter(([, value]) => value !== true)
        .map(([key]) => key);

      // eslint-disable-next-line no-console
      console.log('[Release] 출시 조건 미충족', {
        projectId,
        releaseConditionChecks,
        failedReleaseConditions,
        isFetchingReadyToRelease
      });

      openModal({
        title: '출시 조건을 확인해주세요!',
        children: (
          <>
            <ul css={MarginBottom1em}>
              <Typography as="li" type="body">
                {readyToRelease.isRequiredInfosFilled ? (
                  <>
                    <span css={EmojiRightSpace}>ℹ️️</span>
                    <b>게임 필수 정보</b>를 입력했어요
                  </>
                ) : (
                  <>
                    <span css={EmojiRightSpace}>❌</span>
                    <b>게임 필수 정보</b>를 입력하셨나요?
                  </>
                )}
              </Typography>
              <Typography as="li" type="body">
                {readyToRelease.hasFullScenario ? (
                  <>
                    <span css={EmojiRightSpace}>🎮</span>
                    <b>게임 시작과 게임 종료 리액션</b>을 넣었어요
                  </>
                ) : (
                  <>
                    <span css={EmojiRightSpace}>❌</span>
                    <b>게임 시작과 게임 종료 리액션</b>을 넣으셨나요?
                  </>
                )}
              </Typography>
              <Typography as="li" type="body">
                {readyToRelease.hasVisibleMission ? (
                  <>
                    <span css={EmojiRightSpace}>🎮</span>
                    <b>한 개 이상의 미션이 &apos;표시 상태&apos;</b>에요
                  </>
                ) : (
                  <>
                    <span css={EmojiRightSpace}>❌</span>
                    <b>한 개 이상의 미션이 &apos;표시 상태&apos;</b>인가요?
                  </>
                )}
              </Typography>
              <Typography as="li" type="body">
                {readyToRelease.isPlayed ? (
                  <>
                    <span css={EmojiRightSpace}>🎮</span>
                    <b>게임을 시작부터 마지막</b>까지 플레이했어요
                  </>
                ) : (
                  <>
                    <span css={EmojiRightSpace}>❌</span>
                    <b>게임을 시작부터 마지막</b>까지 플레이해보셨나요?
                  </>
                )}
              </Typography>
            </ul>
            <Typography as="span" type="body">
              출시 조건에 대한 더 자세한 정보는{' '}
              <Button url={helpLink.notion.helpCenter} external>
                헬프센터
              </Button>
              에서 확인해주세요.
            </Typography>
          </>
        ),
        primaryAction: { content: '확인', onAction: closeModal },
        secondaryAction: {
          content: '헬프센터 바로가기',
          onAction: () => window.open(helpLink.notion.helpCenter, '_blank')?.focus()
        }
      });
    }
  }, [app, isBeforeUseReleasePermission, isReadyToRelease, isFetchingReadyToRelease]);

  const isPayedSupporting =
    app?.planType === 'Realworld' || app?.planType === 'Class';

  useEffect(() => {
    if (formState.errors.preparationMessage) {
      openModal({
        title: '필수 항목을 모두 채워주세요',
        cssStyle: TextAlignCenter,
        children: (
          <>
            <Icon
              cssStyle={MarginBottom10}
              icon="times_circle_regular"
              size="100px"
              color={color.interactive_critical_default}
            />
            준비 안내 문구가 비어있어요
          </>
        ),
        primaryAction: { content: '확인', onAction: closeModal }
      });
    }

    if (formState.errors.pointPrice || formState.errors.originalPointPrice) {
      openModal({
        title: '판매 가격을 다시 설정해주세요',
        cssStyle: ModalChildrenStyles,
        primaryAction: {
          content: '확인',
          onAction: closeModal
        },
        children: (
          <>
            <Icon
              cssStyle={MarginBottom10}
              icon="times_circle_regular"
              size="100px"
              color={color.interactive_critical_default}
            />
            최소 20 츄로 이상으로 설정해주세요.
          </>
        )
      });
    }
  }, [formState.submitCount]);

  const openDeleteProjectModal = () => {
    if (!appId || !projectId) {
      return;
    }

    const projectName = readyToRelease.project?.name || '';
    const projectStatus = releaseStatus?.data.status || '';

    openModal({
      size: 'medium',
      title: '이 콘텐츠를 삭제하시겠어요?',
      hasFooter: false,
      isBottomRadius: true,
      children: (
        <DeleteProjectModal
          projectName={projectName}
          onDelete={() => {
            deleteProject(projectId);
            track.onClick({
              clickEventName: ClickEventName.click_channel_ai_project_delete_modal_delete,
              params: {
                channel_id: appId,
                project_id: projectId,
                project_name: projectName,
                status: projectStatus
              }
            });
          }}
          onCancel={() => {
            closeModal();
            track.onClick({
              clickEventName: ClickEventName.click_channel_ai_project_delete_modal_cancel,
              params: {
                channel_id: appId,
                project_id: projectId,
                project_name: projectName,
                status: projectStatus
              }
            });
          }}
        />
      )
    });
  };

  const deleteProject = async (targetProjectId: string) => {
    if (isDeletingProject || !appId) {
      return; // 중복 요청 방지
    }

    try {
      await mutateByPath(`/apps/${appId}/projects/${targetProjectId}`, 'DELETE');
      // 삭제 성공 시 상태를 업데이트하여 Redirect 컴포넌트가 처리하도록 함
      setShouldRedirect(`/apps/${appId}`);
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        toast({ message: '이미 삭제된 콘텐츠입니다.', type: 'error' });
      } else {
        toast({ message: '콘텐츠 삭제에 실패했어요. 다시 시도해주세요.', type: 'error' });
      }
    }
  };

  if (isFetchingReadyToRelease || !releaseStatus?.data || redirectTo === undefined || !app)
    return <LoadingSpinner />;

  if (typeof redirectTo === 'string') return <Redirect to={redirectTo} />;

  if (shouldRedirect) {
    return <Redirect to={shouldRedirect} />;
  }

  return (
    <>
      {((app.channelType === 'Business' &&
        (!app.releasePermission || isBeforeUseReleasePermission)) ||
        isReadyToRelease === false) && <div css={Backdrop} />}
      <form
        css={[container960style, SpacingLayout, SpacingForBottomFloatingBar]}
        onSubmit={onSubmit}
      >
        <Banner
          header={
            <span>
              출시 전{' '}
              <a href={helpLink.notion.guideline} target="_blank" rel="noreferrer" css={ATagStyle}>
                커뮤니티 가이드라인
              </a>
              을 확인해주세요!
            </span>
          }
        >
          선정적이거나 불쾌감을 주는 이미지, 타인의 저작권이나 개인정보 또는 초상권을 침해하는 내용,
          위험한 행동을 조장하는 내용은 게시할 수 없으며 위반할 경우 관리자에게 제재를 받을 수
          있어요.
        </Banner>
        {(app.planType === 'Enterprise' || app.planType === 'Class') && (
          <Banner
            iconComponent={<Icon icon="check_circle_solid" size="20px" color="#7800FF" />}
            header={
              <span>
                <Typography
                  as="span"
                  type="heading"
                  cssStyle={css`
                    background: linear-gradient(90deg, #7800ff 0%, #829bff 90%);
                    background-clip: text;
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                  `}
                >
                  {PLAN_NAME_MAP[app.planType]} 요금제 혜택
                </Typography>
                이 적용되었어요
              </span>
            }
          >
            <ul
              css={css`
                list-style: disc;
                margin-left: 20px;
              `}
            >
              <li>
                앱 설치 없이도{' '}
                <Tooltip
                  title={
                    <span>
                      <Typography
                        as="span"
                        type="subheading"
                        cssStyle={css`
                          color: #d53d40;
                        `}
                      >
                        &apos;웹 플레이&apos; 불가
                      </Typography>{' '}
                      여기 괄호한 곳 액션(기능)을 확인해주세요! 아래 액션이 포함된 콘텐츠는 웹에서
                      플레이가 제한될 수 있어요.
                    </span>
                  }
                  content={
                    <ul
                      css={css`
                        list-style: disc;
                        margin-left: 20px;
                      `}
                    >
                      <li>
                        <Typography as="span" type="body">
                          방향 입력
                        </Typography>
                      </li>
                      <li>
                        <Typography as="span" type="body">
                          AR 이미지 인식
                        </Typography>
                      </li>
                      <li>
                        <Typography as="span" type="body">
                          QR 코드 인식
                        </Typography>
                      </li>
                      <li>
                        <Typography as="span" type="body">
                          컬러 필터 렌즈
                        </Typography>
                      </li>
                      <li>
                        <Typography as="span" type="body">
                          오버랩
                        </Typography>
                      </li>
                      <li>
                        <Typography as="span" type="body">
                          NFC 태그 인식
                        </Typography>
                      </li>
                      <li>
                        <Typography as="span" type="body">
                          GPS 기능
                        </Typography>
                      </li>
                    </ul>
                  }
                  cssStyle={css`
                    width: 247px;
                  `}
                >
                  <Typography
                    as="span"
                    type="body"
                    cssStyle={css`
                      color: #7800ff;
                      font-weight: ${typography.weight.bold};
                      text-decoration: underline;
                      cursor: default;
                    `}
                  >
                    웹 플레이
                  </Typography>
                </Tooltip>
                가 가능해요!
              </li>
              <li>30초간 재생되는 시작 광고, 하단 상시 노출 광고가 제거되었어요!</li>
            </ul>
          </Banner>
        )}
        <Card>
          <Controller
            control={control}
            name="status"
            defaultValue={releaseStatus?.data.status}
            render={({ field: { value, onChange } }) => (
              <>
                <FormGroup cssStyle={[RadioGroupStyle]} label="출시 상태">
                  <ButtonGroup segmented>
                    <Button
                      type={value === 'Unreleased' ? 'primary' : 'outlinePrimary'}
                      pressed={value === 'Unreleased'}
                      onClick={() => {
                        onChange('Unreleased');
                        track.onClick({
                          clickEventName: ClickEventName.click_releasemanage_select_openornot,
                          params: { status: '미공개' }
                        });
                      }}
                    >
                      미공개
                    </Button>
                    <Button
                      type={value === 'Preparing' ? 'primary' : 'outlinePrimary'}
                      onClick={() => {
                        onChange('Preparing');
                        track.onClick({
                          clickEventName: ClickEventName.click_releasemanage_select_openornot,
                          params: { status: '준비중' }
                        });
                      }}
                      pressed={value === 'Preparing'}
                    >
                      준비중
                    </Button>
                    <Button
                      type={value === 'Released' ? 'primary' : 'outlinePrimary'}
                      onClick={() => {
                        onChange('Released');
                        track.onClick({
                          clickEventName: ClickEventName.click_releasemanage_select_openornot,
                          params: { status: '게임 공개' }
                        });
                      }}
                      pressed={value === 'Released'}
                    >
                      공개
                    </Button>
                  </ButtonGroup>

                  {value && STATUS_DESCRIPTION[value as keyof typeof STATUS_DESCRIPTION] && (
                    <div css={StatusDescriptionContainer}>
                      <Typography as="span" type="body" css={StatusDescriptionTitle}>
                        {STATUS_DESCRIPTION[value as keyof typeof STATUS_DESCRIPTION].title}
                      </Typography>
                      <ul css={StatusDescriptionList}>
                        {STATUS_DESCRIPTION[value as keyof typeof STATUS_DESCRIPTION].contents.map(
                          (item) => (
                            <li key={item.id} css={StatusDescriptionItem}>
                              <Typography as="span" type="body">
                                {item.content}
                              </Typography>
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}

                  {value === 'Preparing' && (
                    <Controller
                      control={control}
                      name="preparationMessage"
                      defaultValue={releaseStatus?.data.preparationMessage}
                      rules={{ required: true }}
                      render={({ field: { value, onChange }, fieldState: { invalid } }) => (
                        <FormGroup
                          label="준비 안내 문구"
                          requiredIndicator
                          errorText={invalid ? '필수 항목이에요' : undefined}
                        >
                          <Input
                            maxLength={150}
                            value={value}
                            onChange={(...args) => {
                              onChange(...args);
                              track.onInputThrottled({
                                inputEventName:
                                  InputEventName.input_releasemanage_ready_readyannouncement
                              });
                            }}
                            error={invalid}
                            placeholder="예) 테스트 중이에요!"
                          />
                        </FormGroup>
                      )}
                    />
                  )}
                </FormGroup>
                <FormGroup cssStyle={[RadioGroupStyle, MarginTop24]} label="판매 정보 설정">
                  <Controller
                    control={control}
                    name="isPaid"
                    defaultValue={hasPaidPrice}
                    render={({ field: { value, onChange } }) => (
                      <ToggleButton
                        onChange={onChange}
                        checked={value ?? hasPaidPrice}
                        cssStyle={[
                          !isPayedSupporting ? DisabledToggleButtonStyle : AbleToggleButtonStyle
                        ]}
                        disabled={!isPayedSupporting}
                      >
                        유료로 판매하기
                      </ToggleButton>
                    )}
                  />
                  {isPayedSupporting && isPaid === true && (
                    <div css={[RowFlex, OnMobileFlexColumn, MarginTop24]}>
                      <FormGroup label="판매가" cssStyle={FormGroupContentLayout}>
                        <Controller
                          control={control}
                          rules={priceFieldRules}
                          name={isKitRequired ? 'price' : 'pointPrice'}
                          defaultValue={(isKitRequired
                            ? releaseStatus?.data.price
                            : releaseStatus?.data.pointPrice
                          )?.toString()}
                          render={({ field: { value, onChange } }) => (
                            <Input
                              inputMode="decimal"
                              value={inputNumberWithCommaFormatter(value)}
                              onChange={(...args) => {
                                onChange(...args);
                                track.onInputThrottled({
                                  inputEventName:
                                    InputEventName.input_releasemanage_salesetting_payprice,
                                  params: { price: args[0] }
                                });
                              }}
                              placeholder={isKitRequired ? '25,000' : '5'}
                            />
                          )}
                        />
                        {isKitRequired ? (
                          <span className="currency" css={textStyleBody}>
                            원
                          </span>
                        ) : (
                          <span className="currency" css={textStyleBody}>
                            츄로
                          </span>
                        )}
                      </FormGroup>
                      <Controller
                        control={control}
                        rules={{
                          validate: (value) => {
                            if (isKitRequired) return true;
                            if (value === undefined) return true;

                            const parsedPrice = parseInt(value, 10);
                            if (Number.isNaN(parsedPrice) || parsedPrice === 0) return true;

                            return parsedPrice >= 20;
                          }
                        }}
                        name={isKitRequired ? 'originalPrice' : 'originalPointPrice'}
                        defaultValue={(isKitRequired
                          ? releaseStatus?.data.originalPrice
                          : releaseStatus?.data.originalPointPrice
                        )?.toString()}
                        render={({ field: { value, onChange } }) => (
                          <FormGroup
                            label="할인 전 가격 (할인 중일 시에만 등록)"
                            cssStyle={[MarginLeft16, FormGroupContentLayout]}
                          >
                            <Input
                              inputMode="decimal"
                              value={inputNumberWithCommaFormatter(value)}
                              onChange={(...args) => {
                                onChange(...args);
                                track.onInputThrottled({
                                  inputEventName:
                                    InputEventName.input_releasemanage_salesetting_discountprice,
                                  params: { originalPrice: args[0] }
                                });
                              }}
                              placeholder={isKitRequired ? '25,000' : '5'}
                            />
                            {isKitRequired ? (
                              <span className="currency" css={textStyleBody}>
                                원
                              </span>
                            ) : (
                              <span className="currency" css={textStyleBody}>
                                츄로
                              </span>
                            )}
                          </FormGroup>
                        )}
                      />
                    </div>
                  )}
                </FormGroup>
              </>
            )}
          />
        </Card>
        <Button
          type="plainDestructive"
          cssStyle={css`
            margin-top: ${spacing.margin.xlarge2};
          `}
          onClick={() => {
            if (!projectId) {
              return;
            }

            openDeleteProjectModal();
          }}
        >
          콘텐츠 삭제
        </Button>
      </form>
      <BottomFloatingBar onSave={onSubmit} loading={formState.isSubmitting} />
      <Modal
        {...modal}
        {...(modal.hasFooter !== false &&
          modal.primaryAction && {
            primaryAction: {
              ...modal.primaryAction,
              loading: isSubmitting
            }
          })}
      />
    </>
  );
}

const DisabledToggleButtonStyle = css`
  opacity: 0.5;
`;

const AbleToggleButtonStyle = css`
  opacity: 1;
`;

const Backdrop = css`
  position: fixed;
  z-index: 3;
  background: rgba(0, 0, 0, 0.25);
  width: 100%;
  height: 100%;
`;

const SpacingLayout = css`
  > *:not(:last-of-type) {
    margin-bottom: ${spacing.margin.xlarge2};
  }
`;

const RadioGroupStyle = css`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.margin.small} ${spacing.margin.xlarge2};

  > div:first-of-type > label {
    width: 120px;
  }
`;

const StatusDescriptionContainer = css`
  margin: ${spacing.margin.medium} 0;
`;

const StatusDescriptionTitle = css`
  color: ${color.text_default};
`;

const StatusDescriptionList = css`
  list-style: none;
  padding-left: ${spacing.padding.small};
  margin: 0;
`;

const StatusDescriptionItem = css`
  position: relative;
  padding-left: ${spacing.padding.large};

  &:last-child {
    margin-bottom: 0;
  }

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: ${color.text_subdued};
  }
`;

const FormGroupContentLayout = css`
  flex: 1 0;

  ${onMobile} {
    margin-top: ${spacing.margin.large};
    margin-left: 0;
  }

  > div:last-of-type {
    display: flex;
    align-items: center;

    > div {
      width: 100%;
    }

    button,
    .currency {
      margin-left: ${spacing.margin.small};
    }
  }
`;

const MarginBottom1em = css`
  margin-bottom: 1em;
`;
